import { isHtmlInputElement } from '../utils/helpers.js';

function toNumber(value, fallback = 0) {
	const num = typeof value === 'number' ? value : Number.parseFloat((value ?? '').toString());
	return Number.isFinite(num) ? num : fallback;
}

function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value));
}

function isHtmlElement(value) {
	return typeof HTMLElement !== 'undefined' && value instanceof HTMLElement;
}

function closestDialog(el) {
	if (!el?.closest) return null;
	const dialog = el.closest('dialog');
	return dialog instanceof HTMLDialogElement ? dialog : null;
}

function dispatchInputEvents(el) {
	el.dispatchEvent(new Event('input', { bubbles: true }));
	el.dispatchEvent(new Event('change', { bubbles: true }));
}

export const priceRange = {
	formatCurrency(value) {
		const amount = Math.round(toNumber(value, 0));
		return `$${amount.toLocaleString('en-US')}`;
	},

	async mount(el, priceMinSignal, priceMaxSignal, options = {}) {
		if (!isHtmlElement(el)) return;

		// If the slider lives inside a closed <dialog>, defer init until it's opened.
		const dialog = closestDialog(el);
		if (dialog && !dialog.open) return;

		// If element isn't visible/layoutable yet, defer init.
		const rect = el.getBoundingClientRect?.();
		if (!rect || rect.width <= 0) return;

		const min = toNumber(options.min ?? el.dataset.min, 0);
		const max = toNumber(options.max ?? el.dataset.max, 100);
		const step = Math.max(0, toNumber(options.step ?? el.dataset.step ?? 1, 1));
		const defaultMin = toNumber(options.defaultMin ?? el.dataset.defaultMin, min);
		const defaultMax = toNumber(options.defaultMax ?? el.dataset.defaultMax, max);

		const root = el.closest('dialog, form, section, body') ?? document;
		const minValueEl = root.querySelector('[data-price-min-value]');
		const maxValueEl = root.querySelector('[data-price-max-value]');
		const minDisplayEl = root.querySelector('[data-price-min-display]');
		const maxDisplayEl = root.querySelector('[data-price-max-display]');

		const clampInterval = (from, to) => {
			let nextFrom = clamp(toNumber(from, defaultMin), min, max);
			let nextTo = clamp(toNumber(to, defaultMax), min, max);

			if (step > 0) {
				nextFrom = min + Math.round((nextFrom - min) / step) * step;
				nextTo = min + Math.round((nextTo - min) / step) * step;
				nextFrom = clamp(nextFrom, min, max);
				nextTo = clamp(nextTo, min, max);
			}

			if (nextFrom > nextTo) {
				const temp = nextFrom;
				nextFrom = nextTo;
				nextTo = temp;
			}

			return [nextFrom, nextTo];
		};

		const setOutputs = ([nextFrom, nextTo], { dispatch = false } = {}) => {
			if (isHtmlInputElement(minValueEl)) {
				minValueEl.value = String(nextFrom);
				if (dispatch) dispatchInputEvents(minValueEl);
			}
			if (isHtmlInputElement(maxValueEl)) {
				maxValueEl.value = String(nextTo);
				if (dispatch) dispatchInputEvents(maxValueEl);
			}
			if (isHtmlInputElement(minDisplayEl)) minDisplayEl.value = this.formatCurrency(nextFrom);
			if (isHtmlInputElement(maxDisplayEl)) maxDisplayEl.value = this.formatCurrency(nextTo);
		};

		const readDesiredFromSignalsOrInputs = () => {
			const signalFrom = Number.isFinite(priceMinSignal) ? priceMinSignal : undefined;
			const signalTo = Number.isFinite(priceMaxSignal) ? priceMaxSignal : undefined;

			const inputFrom = isHtmlInputElement(minValueEl) && minValueEl.value !== '' ? minValueEl.value : undefined;
			const inputTo = isHtmlInputElement(maxValueEl) && maxValueEl.value !== '' ? maxValueEl.value : undefined;

			const from = signalFrom ?? inputFrom ?? defaultMin;
			const to = signalTo ?? inputTo ?? defaultMax;

			return clampInterval(from, to);
		};

		// Already initialised: just sync to current signals.
		if (el._rangeSliderApi && typeof el._rangeSliderApi.value === 'function') {
			const next = readDesiredFromSignalsOrInputs();
			el._rangeSliderApi.value(next);
			setOutputs(next, { dispatch: false });
			return;
		}

		// Prevent double-init
		if (el.dataset.priceRangeInitialized === '1') return;
		el.dataset.priceRangeInitialized = '1';
		el._priceRangeCleanup?.();

		const initial = readDesiredFromSignalsOrInputs();

		try {
			const { default: rangeSlider } = await import('range-slider-input');

			const api = rangeSlider(el, {
				min,
				max,
				step,
				value: initial,
				onInput: (value, userInteraction) => {
					const next = clampInterval(value?.[0], value?.[1]);
					setOutputs(next, { dispatch: Boolean(userInteraction) });

					// Dispatch custom event for Alpine store sync
					if (userInteraction) {
						el.dispatchEvent(new CustomEvent('price-change', {
							bubbles: true,
							detail: { min: next[0], max: next[1] }
						}));
					}
				},
				...options,
			});

			el._rangeSliderApi = api;

			// Initial paint
			setOutputs(clampInterval(...api.value()), { dispatch: false });
		} catch (e) {
			// Allow retry (e.g. module load failure)
			delete el.dataset.priceRangeInitialized;
			el._priceRangeCleanup?.();
			delete el._priceRangeCleanup;
			throw e;
		}
	},

	refresh(el) {
		if (!isHtmlElement(el)) return;
		const api = el._rangeSliderApi;
		if (!api || typeof api.value !== 'function') return;
		api.value(api.value());
	},

	autoInit(root = document) {
		if (typeof window === 'undefined') return;
		const scope = root && 'querySelectorAll' in root ? root : document;

		const sliders = Array.from(scope.querySelectorAll('[data-price-range]'));
		for (const el of sliders) {
			this.mount(el).catch(() => null);
		}
	},

	watchDialogs(root = document) {
		if (typeof window === 'undefined') return;
		const scope = root && 'querySelectorAll' in root ? root : document;

		const dialogs = Array.from(scope.querySelectorAll('dialog'));
		if (!dialogs.length) return;

		for (const dialog of dialogs) {
			if (dialog.dataset.priceRangeDialogObserved === '1') continue;
			dialog.dataset.priceRangeDialogObserved = '1';

			const observer = new MutationObserver(() => {
				if (!dialog.open) return;

				this.autoInit(dialog);
				requestAnimationFrame(() => {
					const sliders = Array.from(dialog.querySelectorAll('[data-price-range]'));
					for (const el of sliders) this.refresh(el);
				});
			});

			observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });

			if (dialog.open) {
				this.autoInit(dialog);
				requestAnimationFrame(() => {
					const sliders = Array.from(dialog.querySelectorAll('[data-price-range]'));
					for (const el of sliders) this.refresh(el);
				});
			}
		}
	},
};

if (typeof window !== 'undefined') {
	window.priceRange = priceRange;
}
